import logger from '../../../utils/logger';
import { Material } from '../../../../types/api/Material';
import { MixDesignItem } from '../../../../types/batch/MixDesignItem';

export const updateMixDesignForSubstitution = (
  runningData: any,
  originalMaterial: Material,
  substituteMaterial: Material,
  scaleData: any,
  currentWeight: number,
): void => {
  if (!runningData.mixDesign || currentWeight <= 0) {
    return;
  }

  const originalTarget = scaleData.mft;
  const remainingWeight = originalTarget - currentWeight;

  logger.info('[MIX_DESIGN_UPDATE] Starting mixDesign update for material swap', {
    originalMaterial: originalMaterial.code,
    substituteMaterial: substituteMaterial.code,
    originalTarget,
    actualDispensed: currentWeight,
    remainingToDispense: remainingWeight,
  });

  updateOriginalMaterialWeight(
    runningData.mixDesign,
    originalMaterial,
    substituteMaterial,
    currentWeight,
  );

  handleSubstituteMaterial(
    runningData.mixDesign,
    originalMaterial,
    substituteMaterial,
    scaleData,
    remainingWeight,
  );

  logFinalMixDesignState(runningData.mixDesign);
};

const updateOriginalMaterialWeight = (
  mixDesign: MixDesignItem[],
  originalMaterial: Material,
  substituteMaterial: Material,
  currentWeight: number,
): void => {
  let originalMaterialUpdated = false;

  mixDesign.forEach((item: MixDesignItem, index: number) => {
    if (isSameMaterial(item, originalMaterial)) {
      const previousActualWeight = mixDesign[index].actualWeight || 0;
      mixDesign[index].actualWeight = currentWeight;
      mixDesign[index].wasSubstituted = true;
      mixDesign[index].substituteMaterialId = substituteMaterial.itemId;
      mixDesign[index].substituteMaterialCode = substituteMaterial.code;

      originalMaterialUpdated = true;

      logger.info('[MIX_DESIGN_UPDATE] Updated original material actual weight', {
        material: originalMaterial.code,
        previousActualWeight,
        newActualWeight: currentWeight,
        target: item.quantityFinal || item.quantity,
      });
    }
  });

  if (!originalMaterialUpdated) {
    logger.warn('[MIX_DESIGN_UPDATE] Original material not found in mixDesign', {
      originalMaterial: originalMaterial.code,
    });
  }
};

const handleSubstituteMaterial = (
  mixDesign: MixDesignItem[],
  originalMaterial: Material,
  substituteMaterial: Material,
  scaleData: any,
  remainingWeight: number,
): void => {
  const substituteIndex = mixDesign.findIndex((item: MixDesignItem) =>
    isSameMaterial(item, substituteMaterial),
  );

  if (substituteIndex !== -1) {
    updateExistingSubstituteMaterial(
      mixDesign,
      substituteIndex,
      substituteMaterial,
      remainingWeight,
    );
  } else {
    addNewSubstituteMaterial(
      mixDesign,
      originalMaterial,
      substituteMaterial,
      scaleData,
      remainingWeight,
    );
  }
};

const updateExistingSubstituteMaterial = (
  mixDesign: MixDesignItem[],
  substituteIndex: number,
  substituteMaterial: Material,
  remainingWeight: number,
): void => {
  const previousWeight = mixDesign[substituteIndex].actualWeight || 0;
  mixDesign[substituteIndex].actualWeight = previousWeight + remainingWeight;
  mixDesign[substituteIndex].includesSubstitution = true;

  logger.info('[MIX_DESIGN_UPDATE] Updated existing substitute material weight', {
    material: substituteMaterial.code,
    previousWeight,
    addedWeight: remainingWeight,
    newTotalWeight: previousWeight + remainingWeight,
  });
};

const addNewSubstituteMaterial = (
  mixDesign: MixDesignItem[],
  originalMaterial: Material,
  substituteMaterial: Material,
  scaleData: any,
  remainingWeight: number,
): void => {
  const newEntry: MixDesignItem = {
    constituentItemId: substituteMaterial.itemId,
    constituentItemCode: substituteMaterial.code,
    batchCode: substituteMaterial.batchCode,
    actualWeight: 0,
    quantity: remainingWeight,
    quantityFinal: remainingWeight,
    unitSymbol: scaleData.constituent?.unitSymbol ||
                originalMaterial.itemBatchUnitSymbol ||
                substituteMaterial.itemBatchUnitSymbol,
    isSubstitute: true,
    originalMaterialId: originalMaterial.itemId,
    originalMaterialCode: originalMaterial.code,
  };

  mixDesign.push(newEntry);

  logger.info('[MIX_DESIGN_UPDATE] Added new substitute material to mixDesign', {
    material: substituteMaterial.code,
    weight: remainingWeight,
    originalMaterial: originalMaterial.code,
  });
};

const isSameMaterial = (item: MixDesignItem, material: Material): boolean => {
  return item.constituentItemId === material.itemId ||
    (!!item.batchCode && !!material.batchCode && item.batchCode === material.batchCode);
};

const logFinalMixDesignState = (mixDesign: MixDesignItem[]): void => {
  logger.info('[MIX_DESIGN_UPDATE] Final mixDesign state after substitution', {
    materials: mixDesign.map((item: MixDesignItem) => ({
      code: item.constituentItemCode,
      actualWeight: item.actualWeight || 0,
      target: item.quantityFinal || item.quantity,
      wasSubstituted: item.wasSubstituted || false,
      isSubstitute: item.isSubstitute || false,
    })),
  });
};
